// useScoringConfiguration.ts - Enhanced configuration hook with caching and error handling

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  ScoringConfiguration, 
  ScoringValidationResult, 
  UIThresholds, 
  RecommendationTriggers 
} from '../types/ScoringConfiguration';
import { ScoringConfigurationService } from '../services/ScoringConfigurationService';

interface ConfigurationCache {
  configurations: ScoringConfiguration[];
  activeConfig: ScoringConfiguration | null;
  lastFetch: number;
  ttl: number; // Time to live in milliseconds
}

interface ConfigurationError {
  type: 'LOAD_ERROR' | 'SAVE_ERROR' | 'VALIDATION_ERROR' | 'NETWORK_ERROR';
  message: string;
  details?: any;
}

export interface ScoringConfigurationHookResult {
  // Configuration data
  configurations: ScoringConfiguration[];
  activeConfig: ScoringConfiguration | null;
  
  // Loading and error states
  loading: boolean;
  error: ConfigurationError | null;
  isValidating: boolean;
  
  // Configuration operations
  refreshConfigurations: () => Promise<void>;
  setActiveConfiguration: (configId: string) => Promise<void>;
  saveConfiguration: (config: ScoringConfiguration) => Promise<ScoringConfiguration>;
  deleteConfiguration: (configId: string) => Promise<void>;
  validateConfiguration: (config: ScoringConfiguration) => ScoringValidationResult;
  
  // Helper methods
  getConfigurationById: (id: string) => ScoringConfiguration | null;
  getUIThresholds: () => UIThresholds;
  getRecommendationTriggers: () => RecommendationTriggers;
  
  // Cache management
  clearCache: () => void;
  getCacheStatus: () => { cached: boolean; age: number; ttl: number };
}

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second

export const useScoringConfiguration = (): ScoringConfigurationHookResult => {
  // State management
  const [configurations, setConfigurations] = useState<ScoringConfiguration[]>([]);
  const [activeConfig, setActiveConfig] = useState<ScoringConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ConfigurationError | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  
  // Cache management
  const cacheRef = useRef<ConfigurationCache>({ 
    configurations: [], 
    activeConfig: null, 
    lastFetch: 0, 
    ttl: CACHE_TTL 
  });
  
  const retryCountRef = useRef(0);

  // Error handling helper
  const handleError = useCallback((type: ConfigurationError['type'], message: string, details?: any) => {
    const error: ConfigurationError = { type, message, details };
    setError(error);
    console.error(`Configuration ${type}:`, message, details);
  }, []);

  // Cache validation
  const isCacheValid = useCallback((): boolean => {
    const now = Date.now();
    const cache = cacheRef.current;
    return cache.lastFetch > 0 && (now - cache.lastFetch) < cache.ttl;
  }, []);

  // Clear cache
  const clearCache = useCallback(() => {
    cacheRef.current = {
      configurations: [],
      activeConfig: null,
      lastFetch: 0,
      ttl: CACHE_TTL
    };
  }, []);

  // Retry logic with exponential backoff
  const withRetry = useCallback(async <T>(
    operation: () => Promise<T>, 
    context: string
  ): Promise<T> => {
    for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        const result = await operation();
        retryCountRef.current = 0; // Reset on success
        return result;
      } catch (err) {
        retryCountRef.current = attempt + 1;
        
        if (attempt === MAX_RETRY_ATTEMPTS - 1) {
          throw err; // Final attempt failed
        }
        
        // Wait before retry with exponential backoff
        const delay = RETRY_DELAY * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    throw new Error(`Failed after ${MAX_RETRY_ATTEMPTS} attempts`);
  }, []);

  // Load configurations with caching
  const loadConfigurations = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && isCacheValid()) {
      const cache = cacheRef.current;
      setConfigurations(cache.configurations);
      setActiveConfig(cache.activeConfig);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [allConfigs, activeConfigData] = await withRetry(async () => {
        return Promise.all([
          ScoringConfigurationService.getAll(),
          ScoringConfigurationService.getActive()
        ]);
      }, 'load configurations');

      // Update cache
      cacheRef.current = {
        configurations: allConfigs,
        activeConfig: activeConfigData,
        lastFetch: Date.now(),
        ttl: CACHE_TTL
      };

      setConfigurations(allConfigs);
      setActiveConfig(activeConfigData);
    } catch (err) {
      handleError('LOAD_ERROR', 'Failed to load configurations', err);
      
      // Fallback to default configuration
      try {
        const defaultConfig = ScoringConfigurationService.getDefaultConfiguration();
        setConfigurations([defaultConfig]);
        setActiveConfig(defaultConfig);
      } catch (fallbackErr) {
        handleError('LOAD_ERROR', 'Failed to load default configuration', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  }, [isCacheValid, withRetry, handleError]);

  // Refresh configurations
  const refreshConfigurations = useCallback(async () => {
    await loadConfigurations(true);
  }, [loadConfigurations]);

  // Set active configuration
  const setActiveConfiguration = useCallback(async (configId: string) => {
    try {
      setError(null);
      
      await withRetry(async () => {
        await ScoringConfigurationService.setActive(configId);
      }, 'set active configuration');

      // Update local state and cache
      const newActiveConfig = configurations.find(c => c.id === configId) || null;
      setActiveConfig(newActiveConfig);
      
      if (cacheRef.current.lastFetch > 0) {
        cacheRef.current.activeConfig = newActiveConfig;
      }
    } catch (err) {
      handleError('SAVE_ERROR', 'Failed to set active configuration', err);
    }
  }, [configurations, withRetry, handleError]);

  // Save configuration
  const saveConfiguration = useCallback(async (config: ScoringConfiguration): Promise<ScoringConfiguration> => {
    try {
      setError(null);
      setIsValidating(true);

      // Validate before saving
      const validation = ScoringConfigurationService.validate(config);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      const savedConfig = await withRetry(async () => {
        return ScoringConfigurationService.save(config);
      }, 'save configuration');

      // Update local state
      const updatedConfigs = configurations.find(c => c.id === config.id)
        ? configurations.map(c => c.id === config.id ? savedConfig : c)
        : [...configurations, savedConfig];
      
      setConfigurations(updatedConfigs);
      
      // Update cache
      if (cacheRef.current.lastFetch > 0) {
        cacheRef.current.configurations = updatedConfigs;
        if (activeConfig?.id === savedConfig.id) {
          setActiveConfig(savedConfig);
          cacheRef.current.activeConfig = savedConfig;
        }
      }

      return savedConfig;
    } catch (err) {
      if (err instanceof Error && err.message.includes('Validation failed')) {
        handleError('VALIDATION_ERROR', err.message, err);
      } else {
        handleError('SAVE_ERROR', 'Failed to save configuration', err);
      }
      throw err;
    } finally {
      setIsValidating(false);
    }
  }, [configurations, activeConfig, withRetry, handleError]);

  // Delete configuration
  const deleteConfiguration = useCallback(async (configId: string) => {
    try {
      setError(null);

      await withRetry(async () => {
        await ScoringConfigurationService.delete(configId);
      }, 'delete configuration');

      // Update local state
      const updatedConfigs = configurations.filter(c => c.id !== configId);
      setConfigurations(updatedConfigs);

      // Update cache
      if (cacheRef.current.lastFetch > 0) {
        cacheRef.current.configurations = updatedConfigs;
        if (activeConfig?.id === configId) {
          const newActive = updatedConfigs.find(c => c.id === 'default') || updatedConfigs[0] || null;
          setActiveConfig(newActive);
          cacheRef.current.activeConfig = newActive;
        }
      }
    } catch (err) {
      handleError('SAVE_ERROR', 'Failed to delete configuration', err);
    }
  }, [configurations, activeConfig, withRetry, handleError]);

  // Validate configuration
  const validateConfiguration = useCallback((config: ScoringConfiguration): ScoringValidationResult => {
    return ScoringConfigurationService.validate(config);
  }, []);

  // Get configuration by ID
  const getConfigurationById = useCallback((id: string): ScoringConfiguration | null => {
    return configurations.find(c => c.id === id) || null;
  }, [configurations]);

  // Get UI thresholds from active config
  const getUIThresholds = useCallback((): UIThresholds => {
    return activeConfig?.uiThresholds || ScoringConfigurationService.getDefaultConfiguration().uiThresholds;
  }, [activeConfig]);

  // Get recommendation triggers from active config
  const getRecommendationTriggers = useCallback((): RecommendationTriggers => {
    return activeConfig?.recommendationTriggers || ScoringConfigurationService.getDefaultConfiguration().recommendationTriggers;
  }, [activeConfig]);

  // Get cache status
  const getCacheStatus = useCallback(() => {
    const now = Date.now();
    const cache = cacheRef.current;
    return {
      cached: cache.lastFetch > 0,
      age: cache.lastFetch > 0 ? now - cache.lastFetch : 0,
      ttl: cache.ttl
    };
  }, []);

  // Initialize on mount
  useEffect(() => {
    loadConfigurations();
  }, [loadConfigurations]);

  // Auto-refresh on visibility change (when tab becomes active)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isCacheValid()) {
        loadConfigurations();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadConfigurations, isCacheValid]);

  return {
    // Configuration data
    configurations,
    activeConfig,
    
    // Loading and error states
    loading,
    error,
    isValidating,
    
    // Configuration operations
    refreshConfigurations,
    setActiveConfiguration,
    saveConfiguration,
    deleteConfiguration,
    validateConfiguration,
    
    // Helper methods
    getConfigurationById,
    getUIThresholds,
    getRecommendationTriggers,
    
    // Cache management
    clearCache,
    getCacheStatus
  };
};

// Utility hook for UI threshold access
export const useUIThresholds = (): UIThresholds => {
  const { getUIThresholds } = useScoringConfiguration();
  return getUIThresholds();
};

// Utility hook for recommendation triggers access
export const useRecommendationTriggers = (): RecommendationTriggers => {
  const { getRecommendationTriggers } = useScoringConfiguration();
  return getRecommendationTriggers();
};